import { styled, css, keyframes } from '@styledComponents';
import { Text } from '@components/typography';
import { Button } from '@components/Button';
import { CTALink } from '@components/CTALink';
import { variant } from 'styled-system';
import { Flex } from '@components/Flex';
import { JoykitIcon } from '@components/JoykitIcon';
import { TOP_BANNER_HEIGHT } from 'src/layout/Layout.constants';
import { JoykitColor, LinkVariant, Theme } from '@components/themes';
import { themeColor, themeMediaQuery, themeSpace, themeVariant } from '@utils/styledTheme.util';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { animationTransition, animationTransitionExt } from '@utils/animationTransition';
import { JOY_LOGO_WIDTH_DESKTOP, JOY_LOGO_WIDTH_TABLET, JOY_LOGO_WIDTH_MOBILE } from 'src/layout/ParticleJoyLogo.styles';
import { Box } from '@components/Box';
import { Label2 } from '@components/typography/interVariants';

// Apple-like fade in animation for mobile Get Started button
const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * Reusable styles for header navigation buttons (e.g., Get Started)
 * Based on Button (SM) Figma specs: Inter Semi Bold 14px, 40px height, pill shape
 */
export const headerNavButtonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${themeSpace(3)};
  height: 40px;
  padding: 0 ${themeSpace(5)};
  border-radius: 20px;
  font-family: Inter UI, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  ${themeMediaQuery('lg')} {
    font-size: 15px;
  }
`;

export const TOP_BAR_DEFAULT_HEIGHT_DESKTOP = 96;
export const TOP_BAR_COLLAPSED_HEIGHT_DESKTOP = 80;
export const TOP_BAR_DEFAULT_HEIGHT_MOBILE = 56;
export const TOP_BAR_NAV_PADDING_TABLET = 16;
export const TOP_BAR_NAV_PADDING_DESKTOP = 40;

export const StyledNavMenuLinkWrapper = styled(Flex)`
  row-gap: ${themeSpace(5)};
  ${themeMediaQuery('lg')} {
    row-gap: ${themeSpace(6)};
  }
`;

export const StyledNavMenuLink = styled(CTALink)<{ isVisible?: boolean; tabIndex?: number }>`
  width: 100%;
  display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
  span {
    font-style: inherit;
    color: ${themeColor('mono1')};
    &:hover,
    &:active {
      color: ${themeColor('actionPurple3')};
    }
    display: flex;
    column-gap: ${themeSpace(3)};
  }
`;

export const StyledNavMenuLgWrapper = styled(Flex)`
  width: 100%;
  padding-top: ${themeSpace(5)};
  padding-bottom: ${themeSpace(10)};
  padding-left: ${JOY_LOGO_WIDTH_MOBILE}px;
  ${themeMediaQuery('sm')} {
    gap: 32px;
    padding-left: ${JOY_LOGO_WIDTH_TABLET + TOP_BAR_NAV_PADDING_TABLET - 4}px;
  }
  ${themeMediaQuery('md')} {
    gap: 48px;
    padding-left: ${JOY_LOGO_WIDTH_DESKTOP + TOP_BAR_NAV_PADDING_DESKTOP - 4}px;
  }
  ${StyledNavMenuLink}, ${StyledNavMenuLink} {
    min-width: 240px;
  }
`;

export const StyledNavBlogMenuLink = styled(Flex)`
  row-gap: ${themeSpace(3)};
`;

export const StyledNavSubMenuLgWrapper = styled(Flex)`
  width: 100%;
  flex-wrap: wrap;
  gap: ${themeSpace(3)};
  padding-top: ${themeSpace(3)};
  ${themeMediaQuery('md')} {
    column-gap: ${themeSpace(7)};
  }
`;

export const ToggleNavParent = styled(Text)<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${themeSpace(1)} 0px;
  ${themeVariant('menuNav14')}
  ${themeMediaQuery('lg')} {
    ${themeVariant('menuNav16')}
  }
  > span {
    white-space: normal;
    color: ${(props) => (props.isActive ? themeColor('actionPurple3') : 'inherit')};
    transition: color 420ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  svg {
    margin-left: ${themeSpace(1)};
    flex-shrink: 0;
    transform: ${({ isActive }) => (isActive ? 'rotate(-180deg)' : 'none')};
    color: ${(props) => (props.isActive ? themeColor('actionPurple3') : 'inherit')};
    transition: color 420ms cubic-bezier(0.16, 1, 0.3, 1), transform 300ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

export const StyledGetStartedButton = styled(GetStartedButton)`
  ${headerNavButtonStyles}
  border-width: 1px;
`;

export const NavChildText = styled(Text)`
  ${themeVariant('navChild32')}
  line-height: 40px;
  ${themeMediaQuery('lg')} {
    ${themeVariant('navChild36')}
    line-height: 40px;
  }
`;

export const topHeaderLinkHeroTransitionClassName = 'top-header-link-hero-transition';

export const NavMenuDesktopTopBar = styled.nav<{ hiddenMenu: boolean; isScrollCollapsed: boolean }>`
  width: 100%;
  transform: translate(0, ${({ hiddenMenu }) => (hiddenMenu ? '-100%' : '0')});
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px ${themeSpace(7)};
  position: relative;
  z-index: 2;
  transition: transform 350ms cubic-bezier(0.22, 1, 0.36, 1), background-color 350ms cubic-bezier(0.22, 1, 0.36, 1);
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
  ${themeMediaQuery('md')} {
    padding: 0px ${themeSpace(10)};
  }
`;

export const StyledNavMenuDesktopTopBarLeft = styled(Flex)`
  align-items: center;
  column-gap: ${themeSpace(8)};
  ${themeMediaQuery('md')} {
    column-gap: ${themeSpace(9)};
  }
`;

export const StyledNavMenuDesktopTopBarRight = styled(Flex)`
  column-gap: ${themeSpace(5)};
  ${themeMediaQuery('md')} {
    column-gap: ${themeSpace(8)};
  }
`;

export const NavMenuDesktopDropShell = styled(Flex)`
  position: relative;
  left: calc(50%);
  transform: translateX(calc(-50%));
  width: calc(100%);
  transition: ${animationTransition('bottom')};
  padding: 0px calc(${themeSpace(7)} + 4px);
  ${themeMediaQuery('md')} {
    padding: 0px calc(${themeSpace(10)} + 4px);
  }
`;

export const NavMenuDesktopDropShellWrapper = styled.div<{
  isOpen: boolean;
  isOpening: boolean;
  isScrollCollapsed: boolean;
}>`
  position: absolute;
  top: 0;
  width: 100%;
  padding-top: ${TOP_BAR_DEFAULT_HEIGHT_DESKTOP + TOP_BANNER_HEIGHT}px;
  transform: ${({ isOpen, isOpening }) => (isOpen && isOpening ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ isOpen, isOpening }) => (isOpen && isOpening ? 1 : 0)};
  transition: transform 420ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms cubic-bezier(0.16, 1, 0.3, 1);
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const NavMenuMobileTopBar = styled.nav`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 2;
  margin-top: ${themeSpace(7)};
  transition: ${animationTransition('height')}, ${animationTransitionExt({ property: 'background-color', duration: '250ms' })};
`;

export const NavMenuMobileHamburgerImgWrapper = styled.span<{ variant?: LinkVariant }>`
  display: inline-block;
  transition: ${animationTransition('color')};
  svg {
    margin-bottom: -3px;
  }
  ${variant({
    scale: 'links',
    variants: {
      primary: {},
    },
  })}
  color: #333333;
`;

export const NavMenuMobileDropShell = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  overflow: ${({ isOpen }) => (isOpen ? 'auto' : 'hidden')};
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 350ms cubic-bezier(0.22, 1, 0.36, 1), height 350ms cubic-bezier(0.22, 1, 0.36, 1);
`;

export const NavMenuMobileDropShellContainer = styled(Flex)`
  row-gap: ${themeSpace(9)};
  ${themeMediaQuery('below_sm')} {
    row-gap: ${themeSpace(7)};
    padding-top: ${themeSpace(6)};
  }
`;

export const MobileMenuHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: ${TOP_BANNER_HEIGHT}px;
  background-color: white;
  z-index: 10;
  padding: ${themeSpace(6)} ${themeSpace(7)} ${themeSpace(4)};
`;

export const MobileMenuSectionsContainer = styled(Flex)`
  flex-direction: column;
  row-gap: 24px;
  ${themeMediaQuery('below_sm')} {
    row-gap: 48px;
  }
`;

interface HeaderProps {
  theme: Theme;
  isTransparent: boolean;
  isMenuOpen: boolean;
  backgroundColor: JoykitColor;
}

const headerBackground = ({ theme, isTransparent, isMenuOpen, backgroundColor }: HeaderProps): string =>
  isTransparent ? 'rgba(0, 0, 0, 0)' : isMenuOpen ? theme.colors['white'] : theme.colors[backgroundColor || 'mono7'];

const headerMobileBackground = (): string => 'transparent';

export const StyledHeader = styled.header<{
  isTransparent: boolean;
  backgroundColor: JoykitColor;
  isMenuOpen: boolean;
  hiddenMenu: boolean;
  isScrollCollapsed: boolean;
  scrollProgress: number;
}>`
  position: fixed;
  z-index: 100;
  top: 0;
  min-width: 100%;
  transition: ${animationTransition('all')};
  height: ${({ isMenuOpen }) => (isMenuOpen ? '100%' : `${TOP_BAR_DEFAULT_HEIGHT_MOBILE}px`)};

  ${NavMenuDesktopTopBar} {
    height: ${TOP_BAR_DEFAULT_HEIGHT_DESKTOP}px;
    background-color: ${(props) => {
      if (props.isTransparent && props.scrollProgress === 0) return 'rgba(0, 0, 0, 0)';
      if (props.isMenuOpen) return props.theme.colors['white'];
      // Progressive background: transparent to navbar color
      const alpha = props.scrollProgress * 0.85;
      return `rgba(255, 255, 255, ${alpha})`;
    }};
    backdrop-filter: ${(props) => {
      if (props.isTransparent && props.scrollProgress === 0) return 'saturate(100%) blur(0)';
      if (props.isMenuOpen) return 'saturate(180%) blur(20px)';
      // Progressive blur: 0 to 20px based on scroll
      const blurAmount = props.scrollProgress * 20;
      const saturateAmount = 100 + props.scrollProgress * 80;
      return `saturate(${saturateAmount}%) blur(${blurAmount}px)`;
    }};
    transition: background-color 420ms cubic-bezier(0.16, 1, 0.3, 1), backdrop-filter 420ms cubic-bezier(0.16, 1, 0.3, 1);

    .${topHeaderLinkHeroTransitionClassName} {
      color: ${({ isTransparent, theme }) => (isTransparent ? 'inherit' : theme.colors['mono1'])};
      transition: color 420ms cubic-bezier(0.16, 1, 0.3, 1);
      &:hover {
        color: ${({ isTransparent }) => (isTransparent ? 'inherit' : themeColor('actionPurple3'))};
      }
      &:active {
        color: ${({ isTransparent }) => (isTransparent ? 'inherit' : themeColor('actionPurple3'))};
      }
    }
  }

  ${NavMenuMobileTopBar} {
    background-color: ${headerMobileBackground};
    padding-bottom: ${themeSpace(1)};
  }
  ${NavMenuDesktopDropShellWrapper} {
    background-color: ${({ theme }) => theme.colors['white']};
    box-shadow: ${({ isMenuOpen }) =>
      !isMenuOpen ? 'none' : '0px 50px 100px -20px rgba(93, 81, 50, 0.05), 0px 30px 60px -30px rgba(0, 0, 0, 0.06)'};
  }
  ${NavMenuMobileDropShell} {
    background-color: ${({ theme }) => theme.colors['white']};
  }

  .nav-menu-left {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .nav-menu-right a:first-child {
    margin-left: 0;
  }

  .nav-menu-right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  ${themeMediaQuery('sm')} {
    height: ${TOP_BAR_DEFAULT_HEIGHT_DESKTOP}px;
    .nav-menu-left,
    .nav-menu-right {
      height: 58px;
    }
  }
`;

const AnnounceBannerWrapper = styled(Box)`
  position: relative;
  width: 100%;
  z-index: 3;
`;

export const DisplayAboveMobile = styled(AnnounceBannerWrapper)`
  display: block;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const DisplayOnMobile = styled(AnnounceBannerWrapper)`
  display: none;
  ${themeMediaQuery('below_sm')} {
    display: block;
  }
`;

export const NavMenuWrapperMobile = styled.div<{ isScrollCollapsed: boolean }>`
  overflow: hidden;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const MobileGetStartedButton = styled(GetStartedButton)`
  padding: 0 ${themeSpace(6)};
  width: 100%;
  margin-bottom: ${themeSpace(5)};
`;

export const MobileLoginButton = styled(Button)`
  width: 100%;
`;

export const MobileFindButton = styled(Button)`
  margin-top: ${themeSpace(6)};
  justify-content: center;
  width: 100%;
  height: 56px;
`;

export const MobileTopButtonsWrapper = styled(Flex)`
  flex-direction: column;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const MobileLanguageMenu = styled.div`
  ${Label2};

  width: 100%;
  height: 56px;

  font-weight: 600;
  display: flex;
  justify-content: center;
  border-radius: 0px;
  text-underline-offset: 3px;
  background: transparent;

  &:hover {
    background: transparent;
    text-decoration: underline;
  }

  cursor: pointer;
`;

export const HeaderSpacingStub = styled.div`
  height: ${TOP_BAR_DEFAULT_HEIGHT_MOBILE}px;
  ${themeMediaQuery('sm')} {
    height: ${TOP_BAR_DEFAULT_HEIGHT_DESKTOP}px;
  }
`;

export const StyledCloseButton = styled(Button)`
  float: right;
  top: ${themeSpace(6)};
  right: ${themeSpace(6)};
  padding: ${themeSpace(3)};
  background: none;
`;

export const StyledCloseIcon = styled(JoykitIcon)`
  width: ${themeSpace(5)};
  height: ${themeSpace(5)};
`;

export const HamburgerButton = styled(Button)<{ isClosed?: boolean }>`
  padding: ${themeSpace(3)};
  outline: unset !important;
  color: unset;
  line-height: 24px;
  height: 40px;
  width: ${({ isClosed }) => (isClosed ? 'auto' : '50px')};
  /* Unified frosted glass effect */
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border-radius: 64px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  user-select: none;
  touch-callout: none;
  &:hover {
    background: rgba(255, 255, 255, 0.82);
  }
  &:active {
    background: rgba(255, 255, 255, 0.65);
  }
`;

export const MobHeaderContent = styled(Flex)`
  width: 100%;
`;

export const MobGetStartedButton = styled(GetStartedButton)`
  border-radius: 100em;
  margin-right: ${themeSpace(5)};
  line-height: normal;
  height: 36px;
  width: fit-content;
  padding: 0px 16px;
  border: 1px solid rgba(255, 255, 255, 0.5);

  /* Unified frosted glass effect */
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: ${themeColor('mono1')};
  font-weight: 500;
  font-size: 14px;

  /* Apple-like entrance animation */
  animation: ${fadeInScale} 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards;

  &:hover {
    background: rgba(255, 255, 255, 0.82);
  }

  &:active {
    background: rgba(255, 255, 255, 0.65);
    transform: scale(0.98);
  }
`;

export const StyledBannerContainer = styled(Box)`
  height: ${TOP_BANNER_HEIGHT}px;

  ${themeMediaQuery('sm')} {
    position: absolute;
    top: 0;
    width: 100%;
  }

  overflow: hidden;
  max-width: 100vw;
`;
